import type { AnimatedImageDecoder, AnimatedImageHandle, AnimatedImageInfo, DecodedImageFrame, FileSource, StructuredError } from './l3-types';
import { LabError, throwIfAborted } from './l3-errors';

type WorkerResponse =
  | { type: 'opened'; requestId: string; handleId: string; info: AnimatedImageInfo }
  | { type: 'frame'; requestId: string; frameIndex: number; bitmap: ImageBitmap }
  | { type: 'ok'; requestId: string }
  | { type: 'heartbeat'; requestId: string }
  | { type: 'error'; requestId: string; error: StructuredError };

type Pending = {
  resolve: (value: WorkerResponse) => void;
  reject: (reason: Error) => void;
  timeout: number;
  signal: AbortSignal;
  onAbort: () => void;
};

const NO_PROGRESS_TIMEOUT_MS = 30_000;
const MAX_SOURCE_BYTES = 64 * 1024 * 1024;

export class WorkerImageDecoder implements AnimatedImageDecoder {
  private readonly worker = new Worker(new URL('./image-decoder-worker.ts', import.meta.url), { type: 'module' });
  private readonly pending = new Map<string, Pending>();
  private disposed = false;

  constructor() {
    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => this.onMessage(event.data));
    this.worker.addEventListener('error', (event) => this.failAll(new LabError('IMAGE_WORKER_FAILED', 'image-worker', event.message)));
  }

  async inspect(source: FileSource, signal: AbortSignal): Promise<AnimatedImageInfo> {
    const copy = source.data.slice(0);
    const response = await this.call({ type: 'open', source: { ...source, data: copy } }, [copy], signal);
    if (response.type !== 'opened') throw new LabError('IMAGE_PROTOCOL_ERROR', 'image-inspect', 'Image worker returned an unexpected response.');
    await this.call({ type: 'close', handleId: response.handleId }, [], signal);
    return response.info;
  }

  async open(source: FileSource, signal: AbortSignal): Promise<AnimatedImageHandle> {
    if (source.data.byteLength > MAX_SOURCE_BYTES) throw new LabError('SOURCE_TOO_LARGE', 'image-open', `Source exceeds ${MAX_SOURCE_BYTES} bytes.`);
    const response = await this.call({ type: 'open', source }, [source.data], signal);
    if (response.type !== 'opened') throw new LabError('IMAGE_PROTOCOL_ERROR', 'image-open', 'Image worker returned an unexpected response.');
    return new WorkerImageHandle(this, response.handleId, response.info);
  }

  async frame(handleId: string, ticks: number, signal: AbortSignal): Promise<DecodedImageFrame> {
    const response = await this.call({ type: 'frame', handleId, ticks }, [], signal);
    if (response.type !== 'frame') throw new LabError('IMAGE_PROTOCOL_ERROR', 'image-frame', 'Image worker returned an unexpected response.');
    return { frameIndex: response.frameIndex, bitmap: response.bitmap };
  }

  async close(handleId: string): Promise<void> {
    if (this.disposed) return;
    const response = await this.call({ type: 'close', handleId }, [], new AbortController().signal);
    if (response.type !== 'ok') throw new LabError('IMAGE_PROTOCOL_ERROR', 'image-close', 'Image worker returned an unexpected response.');
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.worker.terminate();
    this.failAll(new LabError('IMAGE_WORKER_DISPOSED', 'image-worker', 'Image worker was disposed.'));
  }

  private call(payload: Record<string, unknown>, transfer: Transferable[], signal: AbortSignal): Promise<WorkerResponse> {
    throwIfAborted(signal, 'image-worker');
    if (this.disposed) return Promise.reject(new LabError('IMAGE_WORKER_DISPOSED', 'image-worker', 'Image worker was disposed.'));
    const requestId = crypto.randomUUID();
    return new Promise<WorkerResponse>((resolve, reject) => {
      const fail = (reason: Error) => {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', onAbort);
        this.pending.delete(requestId);
        reject(reason);
      };
      const onAbort = () => fail(new LabError('ABORTED', 'image-worker', 'Operation cancelled.'));
      const timeout = window.setTimeout(() => fail(new LabError('DECODE_NO_PROGRESS_TIMEOUT', 'image-worker', 'Image worker made no progress for 30 seconds.')), NO_PROGRESS_TIMEOUT_MS);
      this.pending.set(requestId, { resolve, reject: fail, timeout, signal, onAbort });
      signal.addEventListener('abort', onAbort, { once: true });
      this.worker.postMessage({ ...payload, requestId }, transfer);
    });
  }

  private onMessage(response: WorkerResponse): void {
    const pending = this.pending.get(response.requestId);
    if (!pending) return;
    if (response.type === 'heartbeat') {
      window.clearTimeout(pending.timeout);
      pending.timeout = window.setTimeout(() => pending.reject(new LabError('DECODE_NO_PROGRESS_TIMEOUT', 'image-worker', 'Image worker made no progress for 30 seconds.')), NO_PROGRESS_TIMEOUT_MS);
      return;
    }
    window.clearTimeout(pending.timeout);
    pending.signal.removeEventListener('abort', pending.onAbort);
    this.pending.delete(response.requestId);
    if (response.type === 'error') {
      pending.reject(new LabError(response.error.code, response.error.stage, response.error.message, response.error.recoverable));
      return;
    }
    pending.resolve(response);
  }

  private failAll(reason: Error): void {
    for (const pending of this.pending.values()) pending.reject(reason);
    this.pending.clear();
  }
}

class WorkerImageHandle implements AnimatedImageHandle {
  private closed = false;

  constructor(private readonly decoder: WorkerImageDecoder, private readonly handleId: string, readonly info: AnimatedImageInfo) {}

  frameAtTicks(ticks: number, signal: AbortSignal): Promise<DecodedImageFrame> {
    if (this.closed) return Promise.reject(new LabError('IMAGE_HANDLE_CLOSED', 'image-frame', 'Image handle is closed.'));
    return this.decoder.frame(this.handleId, ticks, signal);
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    await this.decoder.close(this.handleId);
  }
}
